import React from 'react';
import { Helmet } from 'react-helmet';

const FoodCalculator = () => {
  return (
    <div className="min-h-screen bg-gradient-to-r from-yellow-100 to-orange-100 relative overflow-hidden">
      {/* Background animation elements */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
        {/* Floating food items animation */}
        <div className="absolute animate-float animate-duration-3000 animate-infinite" style={{ left: '10%', top: '20%' }}>
          <span className="text-4xl text-yellow-500">🍎</span>
        </div>
        <div className="absolute animate-float animate-duration-4000 animate-infinite" style={{ left: '30%', top: '40%' }}>
          <span className="text-4xl text-green-500">🥗</span>
        </div>
        <div className="absolute animate-float animate-duration-5000 animate-infinite" style={{ left: '50%', top: '60%' }}>
          <span className="text-4xl text-red-500">🍕</span>
        </div>
        <div className="absolute animate-float animate-duration-6000 animate-infinite" style={{ left: '70%', top: '30%' }}>
          <span className="text-4xl text-blue-500">🍴</span>
        </div>
        <div className="absolute animate-float animate-duration-7000 animate-infinite" style={{ left: '80%', top: '70%' }}>
          <span className="text-4xl text-purple-500">🥑</span>
        </div>
      </div>

      <Helmet>
        <title>Food Calculators | Nutrition and Meal Planning Tools</title>
        <meta name="description" content="Collection of food calculators for tracking nutrition, managing portions, and reducing waste. Make informed dietary decisions with our easy-to-use tools." />
        <meta name="keywords" content="food calculators, calorie calculator, nutrition calculator, portion calculator, food waste calculator, pet food calculator" />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
        <div className="text-center mb-12">
          <div className="header-icon text-4xl text-yellow-600 mb-4">🍽️</div>
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Food Calculators</h1>
          <p className="calculator-count text-xl text-gray-600">11 calculators</p>
        </div>

        <div className="max-w-2xl mx-auto mb-12">
          <div className="calculator-description bg-white p-6 rounded-lg shadow-md">
            <p className="text-gray-600 mb-4">
              Food calculations can be complex, from tracking nutritional values to managing portions and waste. 
              Our comprehensive suite of food calculators makes it simple to handle all your food-related calculations. 
              Whether you're tracking calories, managing pet food portions, or reducing food waste, we've got you 
              covered with easy-to-use tools that help you make informed decisions about nutrition and food management.
            </p>
            <button className="read-more inline-block bg-yellow-500 text-white py-2 px-4 rounded-md hover:bg-yellow-600 transition-colors">
              Read more
            </button>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Food Tracking & Nutrition <span className="emoji ml-2">🥗</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/food-waste" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Food Waste Calculator</h3>
                <p className="text-gray-600">Calculate and track food waste impact on environment and finances</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/food-calorie" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Food Calorie Estimator</h3>
                <p className="text-gray-600">Estimate calorie content of meals and recipes</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/serving-size" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Food Serving Size Calculator</h3>
                <p className="text-gray-600">Calculate proper serving sizes for different foods</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/food-sensitivity" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Food Sensitivity Calculator</h3>
                <p className="text-gray-600">Track and identify food sensitivities</p>
              </a>
            </div>
          </div>
        </div>

        <div className="calculator-section mb-12">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Nutrition & Health <span className="emoji ml-2">🥬</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/food-tracker" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Food Tracker Calculator</h3>
                <p className="text-gray-600">Track daily food intake and nutritional goals</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/nutritional-value" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Nutritional Value Calculator</h3>
                <p className="text-gray-600">Calculate nutritional values of foods and meals</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/food-allergen" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Food Allergen Calculator</h3>
                <p className="text-gray-600">Identify and track food allergens in meals</p>
              </a>
            </div>
          </div>
        </div>

        <div className="calculator-section">
          <h2 className="section-title text-2xl font-bold text-gray-800 mb-6 flex items-center">
            Pet Food Calculators <span className="emoji ml-2">🐾</span>
          </h2>
          <div className="calculator-grid grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/dog-food" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Dog Food Calculator</h3>
                <p className="text-gray-600">Calculate proper dog food portions</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/raw-dog-food" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Raw Dog Food Calculator</h3>
                <p className="text-gray-600">Calculate raw food portions for dogs</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/pet-food-portion" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Pet Food Portion Calculator</h3>
                <p className="text-gray-600">Calculate food portions for various pets</p>
              </a>
            </div>
            <div className="calculator-card bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow">
              <a href="/calculator/quarantine-food" className="calculator-link block">
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Quarantine Food Calculator</h3>
                <p className="text-gray-600">Calculate food needs during quarantine</p>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FoodCalculator;